import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { planDayDate, planStepPost, planStepTitle, planStepWhen } from '../rolloutDayCopy';
import { ChannelPill } from './ChannelPill';
import { useRollout } from './RolloutContext';
import type { PlanStepView, StepTone } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { StepActions } from './StepActions';
import { StepLines } from './StepLines';
import { StepThumb } from './StepThumb';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
import { planAssetLabel } from './planAssetLabel';
/* eslint-enable css-modules/no-unused-class */

const TONE_CLASS: Record<StepTone, string | undefined> = {
  ok: styles.toneOk,
  sch: styles.toneSch,
  bad: styles.toneBad,
  miss: styles.toneMiss,
  plain: undefined,
};

const FORMAT_LABEL = {
  story: rolloutMessages.formatStory,
  post: rolloutMessages.formatPost,
  reel: rolloutMessages.formatReel,
} as const;

interface StepCardProps {
  step: PlanStepView;
  isFirst: boolean;
  isLast: boolean;
}

/**
 * One post step of the run sheet (design rollout-plan a1, b1, e1 `.stp` + `.sc`): the day, the node
 * on the line, the card with the picture, the name and format, the lines, the channel pills and
 * the action. A click on the name or "Open" opens the step sheet (frame c1). A step of an AI reels
 * plan is "AI reel N" on "Day N" (frame f3).
 */
const StepCardComponent: React.FC<StepCardProps> = ({ step, isFirst, isLast }) => {
  const intl = useIntl();
  const { openStep, openStepKey, plan } = useRollout();
  const { day } = step;
  const postOfDay = planStepPost(intl, plan, day.key);
  const open = useCallback(() => openStep(step.key), [openStep, step.key]);
  // The file name in the app language, not the English label of the API (QA lit-15, litr2-3).
  const assetLabel = day.asset === null ? null : planAssetLabel(intl, day.asset);
  const showRevision = step.channels.some((channel) => channel.state !== 'notPlanned') && assetLabel !== null;

  return (
    <div
      className={clsx(
        styles.stp,
        TONE_CLASS[step.tone],
        step.past && step.tone === 'ok' && styles.past,
        isFirst && styles.first,
        isLast && styles.last
      )}
    >
      <div className={clsx(styles.when, plan.kind !== 'reels' && day.offsetDays === 0 && styles.whenZero)}>
        <b>{planStepWhen(intl, plan, day.offsetDays)}</b>
        <small>{planDayDate(intl, step.date)}</small>
        {postOfDay !== null && <small data-testid="reels-step-post">{postOfDay}</small>}
      </div>
      <div className={styles.node}>
        <i />
      </div>
      <div className={styles.body}>
        <div className={clsx(styles.card, openStepKey === step.key && styles.cardOpen)}>
          <StepThumb day={day} />
          <div className={styles.cardText}>
            <div className={styles.name}>
              <button type="button" className={styles.nameButton} onClick={open}>
                {planStepTitle(intl, plan, day)}
              </button>
              <span className={styles.fmt}>{intl.formatMessage(FORMAT_LABEL[day.format])}</span>
              {showRevision && <span className={styles.rv}>{assetLabel}</span>}
              {step.moved && (
                <span className={styles.rv} data-testid="step-moved">
                  {intl.formatMessage(rolloutMessages.stepMoved)}
                </span>
              )}
            </div>
            <StepLines step={step} />
            {step.channels.length > 0 && (
              <div className={styles.pills}>
                {step.channels.map((channel) => (
                  <ChannelPill key={channel.platform} channel={channel} />
                ))}
              </div>
            )}
          </div>
          <StepActions step={step} />
        </div>
      </div>
    </div>
  );
};

export const StepCard = TypedMemo(StepCardComponent);
